import { Injectable } from '@angular/core';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import { BaseService } from '../services';

/**
 * Every internal-note attachment call, in one place.
 *
 * These operations were triplicated across `accounts.service`, `contacts.service`
 * and `quote-order-sample.service` with signatures that had already drifted apart
 * — `downloadFileContent(bodyData)` in two of them versus
 * `downloadFileContent(id)` in the third, so the same-named method took a
 * different argument depending on which service the host happened to inject (and
 * two call sites passed the wrong one, building `note/download/[object Object]`).
 * Those three keep their own methods, so unmigrated call sites still compile.
 *
 * ── The contract (verified against a running API, not just read) ───────────────
 *
 * Multi-attachment support adds NO new endpoint. Every route keeps its path and
 * its old behaviour; the per-attachment operations are reached by adding one
 * optional `attachment_id` query parameter. `:id` is always the NOTE id, so the
 * note's own authorization still gates the operation:
 *
 *   POST   /note                                    create, `attachments[]` on the response
 *   POST   /note/upload            (note_id in body) add attachments to a note, and
 *                                                    persist `note` when the same save
 *                                                    also edited the body; nothing
 *                                                    existing is replaced
 *   GET    /note/preview/:noteId?attachment_id=N     preview THAT attachment → served URL
 *   GET    /note/download/:noteId?attachment_id=N    download THAT attachment → stream
 *   DELETE /note/delete-attachment/:noteId?attachment_id=N  delete THAT attachment
 *
 * Omit `attachment_id` and each behaves exactly as it did before this feature:
 * preview/download resolve the note's FIRST attachment and delete clears ALL of
 * them. That is precisely what a pre-rollout note needs — its file lives on the
 * note's own columns and has no attachment id to send — so the same method serves
 * both cases and the callers need no second code path.
 *
 * The multipart field is `internal_note_attachment`, REPEATED once per file — that
 * is what `multer.array()` reads. The API also accepts `internal_note_attachments`
 * as an alias, but the singular name is the canonical one AND is what an older
 * API build understands, so a console bundle running in front of one still gets
 * its first file saved instead of silently losing every file. Do NOT use a
 * bracketed `internal_note_attachment[]`: multer does not strip the brackets.
 */
@Injectable({ providedIn: 'root' })
export class NoteAttachmentService {
  /**
   * The repeated multipart key. Mirrors `NOTE_ATTACHMENT_FIELDS.FIELD` on the API.
   * (`internal_note_attachments` is accepted there too, as an alias.)
   */
  static readonly FIELD = 'internal_note_attachment';

  /**
   * Preview URLs already resolved this session, keyed note:attachment.
   *
   * The endpoint is idempotent and its answer is stable — it returns a served
   * path under the API's own `/uploads/note`, derived from the attachment's
   * stored file name — so previewing the same file twice does not need a second
   * round trip. Re-opening a preview is then instant instead of showing a spinner
   * for data the session already has.
   */
  private readonly previewUrls = new Map<string, string>();

  /**
   * Requests still in flight, so a double-click (or two rows resolving the same
   * legacy note-scoped URL) issues ONE request and both callers get its result.
   */
  private readonly previewInFlight = new Map<string, Observable<string>>();

  constructor(private baseService: BaseService) {}

  /**
   * Appends every file under the repeated key.
   *
   * A helper rather than something each host writes, because the failure mode is
   * silent: append the wrong key (or a bracketed one) and the request still
   * returns 201 — with no attachments on the note.
   */
  static appendFiles(formData: FormData, files: File[] | null | undefined): FormData {
    for (const file of files ?? []) {
      if (file) formData.append(NoteAttachmentService.FIELD, file);
    }
    return formData;
  }

  /**
   * Creates a note with attachments, or appends attachments to an existing one.
   *
   * `noteId` chooses the endpoint but is NOT put in the URL — `/note/upload` reads
   * `note_id` from the multipart body, so the caller appends it there. Both return
   * the note with its full `attachments[]`.
   *
   * Append the edited `note` body to the FormData on the second form: `/note/upload`
   * persists it, so one request saves the text and the new files together. (It used to
   * ignore the field, which made an edit that changed the text and added a file answer
   * 201 with the text silently dropped.)
   */
  addAttachments(formData: FormData, noteId?: number | null) {
    const endpoint = noteId ? 'note/upload' : 'note';
    return this.baseService.uploadPost(endpoint, formData).pipe(
      map((response: any) => response.data),
      catchError((err) => throwError(() => err)),
    );
  }

  /**
   * Resolves one attachment to a URL the browser can render.
   *
   * Pass `attachmentId` for a specific file; omit it (or pass null, which is what
   * a pre-rollout row carries) to get the note's first attachment, as this route
   * has always behaved.
   *
   * HEIC/HEIF come back already converted to JPG — nothing but Safari can decode
   * them — so it is the returned URL's extension that should be sniffed to choose
   * a preview modal, never the original filename's.
   *
   * `skipLoader` is on: this resolves a URL for a modal that renders its own
   * spinner, so the global page loader would only blank the page behind a preview
   * that is about to open anyway.
   */
  previewAttachment(noteId: number, attachmentId?: number | null): Observable<string> {
    const key = this.previewKey(noteId, attachmentId);

    // Already resolved and still valid — emit synchronously so the caller renders
    // without a request, and without a spinner for data it already has. The API
    // returns a short-lived signed URL (`?exp=<unix seconds>&sig=…`) that answers 404
    // once expired, so a cached URL is reused only while it has 30s+ of life left.
    const cached = this.previewUrls.get(key);
    if (cached && this.hasTimeLeft(cached)) {
      return of(cached);
    }
    this.previewUrls.delete(key);

    const pending = this.previewInFlight.get(key);
    if (pending) {
      return pending;
    }

    const request = this.baseService.get(`note/preview/${noteId}${this.scope(attachmentId)}`, undefined, true).pipe(
      map((response: any) => response.body.data),
      tap((url: string) => {
        if (url) this.previewUrls.set(key, url);
      }),
      catchError((err) => throwError(() => err)),
      // Drop the in-flight entry either way: a failure must not be replayed to
      // every later click as a permanently pending request.
      finalize(() => this.previewInFlight.delete(key)),
      // One HTTP call however many subscribers the same click produces.
      shareReplay({bufferSize: 1, refCount: false}),
    );
    this.previewInFlight.set(key, request);
    return request;
  }

  /**
   * The download URL for one attachment, as a plain string.
   *
   * Kept for the callers that still hand a URL straight to the browser. Prefer
   * {@link downloadAttachmentBlob} for a Download CONTROL: handing this URL to
   * `window.open` opens a tab, and even though the route answers with
   * `Content-Disposition: attachment` the tab is visible while the transfer
   * starts — which is the "it opens the image in a new tab" report.
   */
  downloadAttachmentUrl(noteId: number, attachmentId?: number | null): string {
    return this.baseService.getDownloadFileContent(`note/download/${noteId}${this.scope(attachmentId)}`);
  }

  /**
   * The attachment's BYTES, so a Download control can save the file without
   * navigating anywhere.
   *
   * Why this exists rather than an `<a download>` pointing at the route: the API
   * is a different ORIGIN from the console (`:3000` vs `:4201` locally, and
   * `api2.gatherco.com.au` in every deployed environment), and browsers ignore
   * the `download` attribute on a cross-origin href — the anchor would navigate
   * instead of saving. Reading the bytes here turns it into a same-page blob,
   * which an anchor CAN save, under the right filename.
   *
   * `getBlob` goes through the normal request path, so the auth header and the
   * error interceptor apply exactly as they do to every other call. `connect-src`
   * lists the API origins in both `index.html` and `index.dev.html`, so the read
   * itself is allowed by the CSP.
   */
  downloadAttachmentBlob(noteId: number, attachmentId?: number | null) {
    return this.baseService.getBlob(`note/download/${noteId}${this.scope(attachmentId)}`);
  }

  /**
   * Removes ONE attachment when `attachmentId` is given — the note and its other
   * attachments survive.
   *
   * Omitting `attachmentId` clears EVERY attachment on the note, which is this
   * route's original behaviour and the only thing a pre-rollout row (no
   * attachment id, and only one file by definition) can ask for.
   *
   * `skipLoader` is on: every caller splices the deleted row out of the note it
   * already holds, so the file list updates the moment the response lands. A
   * full-page loader for a single-row edit is what made the delete feel like a
   * reload.
   */
  deleteAttachment(noteId: number, attachmentId?: number | null) {
    return this.baseService.delete(`note/delete-attachment/${noteId}${this.scope(attachmentId)}`, true).pipe(
      map((response) => response),
      tap(() => this.forgetPreview(noteId, attachmentId)),
      catchError((err) => throwError(() => err)),
    );
  }

  /**
   * Drop cached preview URLs for a deleted attachment.
   *
   * The note-scoped key goes too: without an attachment_id the route resolves the
   * note's FIRST attachment, so once any file is removed that answer may name a
   * different one.
   */
  private forgetPreview(noteId: number, attachmentId?: number | null): void {
    this.previewUrls.delete(this.previewKey(noteId, attachmentId));
    this.previewUrls.delete(this.previewKey(noteId, null));
  }

  /** True while a signed preview URL's `exp` is at least 30s away. No `exp` → not reusable. */
  private hasTimeLeft(url: string): boolean {
    try {
      const exp = Number(new URL(url).searchParams.get('exp'));
      return Number.isFinite(exp) && exp * 1000 - Date.now() > 30_000;
    } catch {
      return false;
    }
  }

  /** `note:attachment` — `note:` alone for a pre-rollout row with no id. */
  private previewKey(noteId: number, attachmentId?: number | null): string {
    return `${noteId}:${attachmentId != null ? attachmentId : ''}`;
  }

  /** `?attachment_id=N`, or nothing at all when the caller did not scope the request. */
  private scope(attachmentId?: number | null): string {
    return attachmentId != null ? `?attachment_id=${attachmentId}` : '';
  }
}
